import { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, ExternalLink, MapPin } from 'lucide-react';
import { fadeInUp } from '../../hooks/useScrollAnimation';
import { useLang } from '../context/LanguageContext';
import { pick } from '../../utils/i18n';

const Field = ({ label, children }) => (
  <div>
    <span className="text-secondary text-xs font-semibold">{label}</span>
    <div className="text-gray-600 text-sm leading-relaxed mt-1">{children}</div>
  </div>
);

const ProjectMedia = ({ image, imageFit, alt }) => {
  if (!image) {
    return (
      <div className="w-full h-full bg-primary flex items-center justify-center">
        <img src="/images/logos/hoja-savia-blanca.png" alt="" aria-hidden="true" className="h-1/2 w-auto opacity-20" />
      </div>
    );
  }
  if (imageFit === 'contain') {
    return (
      <div className="w-full h-full flex items-center justify-center p-6">
        <img src={image} alt={alt} loading="lazy" className="max-w-full max-h-full object-contain" />
      </div>
    );
  }
  return (
    <img
      src={image}
      alt={alt}
      loading="lazy"
      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
    />
  );
};

export const Band = ({ tint, children, className = '' }) => (
  <motion.article variants={fadeInUp} className={`group relative isolate ${className}`}>
    {/* Franja de ancho completo que se desvanece hacia los costados */}
    <div
      aria-hidden="true"
      className="absolute inset-y-0 left-1/2 -translate-x-1/2 w-screen -z-10"
      style={{ background: `linear-gradient(90deg, transparent 0%, ${tint} 18%, ${tint} 82%, transparent 100%)` }}
    />
    {children}
  </motion.article>
);

const ProjectRow = ({ project, tint, reverse = false, compact = false }) => {
  const { t, lang } = useLang();
  const [open, setOpen] = useState(false);
  const text = project[lang] || project.es;
  const labels = t.projects;
  const detailsId = `detalle-${project.id}`;

  const hasMore =
    text.projectName ||
    text.actors ||
    (text.results && text.results.length) ||
    text.partners ||
    text.implementation ||
    (project.links && project.links.length);

  return (
    <Band
      tint={tint}
      className={`grid grid-cols-1 lg:grid-cols-12 items-center ${
        compact ? 'gap-5 lg:gap-10 py-10 md:py-12' : 'gap-6 lg:gap-12 py-12 md:py-16'
      }`}
    >
      <div
        className={`relative overflow-hidden aspect-[4/3] ${compact ? 'lg:col-span-5' : 'lg:col-span-7'} ${
          reverse ? 'lg:order-last' : ''
        }`}
      >
        <ProjectMedia image={project.image} imageFit={project.imageFit} alt={text.title} />
      </div>

      <div className={compact ? 'lg:col-span-7' : 'lg:col-span-5'}>
        {text.location && (
          <p className="flex items-start gap-1.5 text-secondary text-sm mb-3">
            <MapPin size={15} className="mt-[2px] flex-shrink-0" aria-hidden="true" />
            <span>{text.location}</span>
          </p>
        )}

        <h4 className={`font-display font-bold text-primary leading-tight ${compact ? 'text-xl md:text-2xl' : 'text-2xl md:text-3xl'}`}>
          {text.title}
        </h4>

        {text.tags && text.tags.length > 0 && (
          <p className="text-secondary text-sm font-medium mt-3">{text.tags.join(' · ')}</p>
        )}

        {text.challenge && (
          <p className={`text-gray-600 leading-relaxed ${compact ? 'text-[15px] mt-4' : 'mt-5'}`}>{text.challenge}</p>
        )}

        {project.logos && project.logos.length > 0 && (
          <div className="flex flex-wrap items-center gap-x-6 gap-y-3 mt-6">
            {project.logos.map((logo) => (
              <img
                key={logo.src}
                src={logo.src}
                alt={pick(logo.name, lang)}
                title={pick(logo.name, lang)}
                loading="lazy"
                className="h-10 w-auto max-w-[120px] object-contain"
              />
            ))}
          </div>
        )}

        {hasMore && (
          <button
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            aria-controls={detailsId}
            className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-primary hover:text-secondary transition-colors"
          >
            {open ? labels.seeLess : labels.seeMore}
            <ArrowRight size={16} aria-hidden="true" className={`transition-transform duration-300 ${open ? '-rotate-90' : 'rotate-90'}`} />
          </button>
        )}

        <motion.div
          id={detailsId}
          initial={false}
          animate={open ? { height: 'auto', opacity: 1 } : { height: 0, opacity: 0 }}
          className="overflow-hidden"
          inert={!open}
        >
          <div className="pt-5 space-y-4">
            {text.projectName && <Field label={labels.fields.project}>{text.projectName}</Field>}

            {text.results && text.results.length > 0 && (
              <Field label={labels.fields.results}>
                <ul className="space-y-1.5">
                  {text.results.map((result) => (
                    <li key={result} className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-accent flex-shrink-0 mt-2" />
                      <span>{result}</span>
                    </li>
                  ))}
                </ul>
              </Field>
            )}

            {text.actors && <Field label={labels.fields.actors}>{text.actors}</Field>}

            {text.implementation && <Field label={labels.fields.implementation}>{text.implementation}</Field>}

            {text.partners && <Field label={labels.fields.partners}>{text.partners}</Field>}

            {project.links && project.links.length > 0 && (
              <div className="flex flex-wrap gap-5 pt-1">
                {project.links.map((link) => (
                  <a
                    key={link.url}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm font-medium text-primary underline underline-offset-4 decoration-accent/50 hover:decoration-accent inline-flex items-center gap-1"
                  >
                    {pick(link.label, lang)}
                    <ExternalLink size={13} aria-hidden="true" />
                  </a>
                ))}
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </Band>
  );
};

export default ProjectRow;
